import React, { FC, createContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CreateEstimateHeader from './components/CreateEstimateHeader';
import CreateEstimateSidebar from './components/CreateEstimateSidebar';
import EstimateCustomer from './components/EstimateCustomer';
import CreateEstimateDetails from './components/CreateEstimateDetails';
import EstimateNotesAndAttachmentsSection from './components/EstimateNotesAndAttachmentsSection';
import EstimateLineItemSection from './components/EstimateLineItemSection';
import EstimateFormRemindersSection from './components/EstimateFormRemindersSection';
import { CreateEditEstimateNavEnum } from '../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectMerchantCustomizations } from 'containers/app/appSlice';
import { Loader } from 'components';
import { CreateEstimateContextType } from '../types';
import {
  selectEstimateReminders,
  selectExpiryDate,
  selectIsInitialisingEstimateFromAPI,
  selectRemindersEnabled,
  updateEstimateReminders,
  updateRemindersEnabled
} from '../estimatesSlice';
import CreateEstimateMoreOptions from './components/CreateEstimateMoreOptions';
import EditReminderModal from 'components/reminder-modal/EditReminderModal';
import { calculateScheduledDate } from '../utils';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';

export const CreateEstimateContext = createContext<CreateEstimateContextType>({
  selectedEstimateTab: CreateEditEstimateNavEnum.CUSTOMER,
  setSelectedEstimateTab: null
});

interface CreateEstimateProps {
  onPreview: () => void;
  onCreateAndShare: () => void;
  isSaveInProgress: boolean;
  isLoadingTaxesAndFees: boolean;
  isFetchingCustomer: boolean;
  isFetchingMerchantCustomisations: boolean;
  merchantCustomisationsSuccess: boolean;
}

const CreateEstimate: FC<CreateEstimateProps> = ({
  onPreview,
  onCreateAndShare,
  isSaveInProgress,
  isLoadingTaxesAndFees,
  isFetchingCustomer,
  isFetchingMerchantCustomisations,
  merchantCustomisationsSuccess
}) => {
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');

  const [selectedEstimateTab, setSelectedEstimateTab] = useState<CreateEditEstimateNavEnum>(
    CreateEditEstimateNavEnum.CUSTOMER
  );

  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const previousExpiryDateRef = useRef<string | null>(null);

  const dispatch = useAppDispatch();
  const isInitialisingEstimateFromAPI = useAppSelector(selectIsInitialisingEstimateFromAPI);
  const reminders = useAppSelector(selectEstimateReminders);
  const remindersEnabled = useAppSelector(selectRemindersEnabled);
  const expiryDate = useAppSelector(selectExpiryDate);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);

  useEffect(() => {
    const tabElem = document.getElementById(selectedEstimateTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  }, [selectedEstimateTab]);

  // Watch expiry date changes and recalculate reminder dates
  useEffect(() => {
    if (reminders.length > 0 && expiryDate) {
      const expiryDateString = expiryDate.toISOString();

      // Only recalculate if expiry date has actually changed
      if (previousExpiryDateRef.current !== expiryDateString) {
        previousExpiryDateRef.current = expiryDateString;

        const updatedReminders = reminders.map(reminder => {
          const newReminderDate = calculateScheduledDate(expiryDateString, reminder.scheduleRelation, reminder.days);
          return {
            ...reminder,
            reminderDate: newReminderDate
          };
        });

        dispatch(updateEstimateReminders(updatedReminders));
      }
    }
  }, [expiryDate, reminders, dispatch]);

  const handleSaveReminders = (updatedReminders: ReminderConfig[]) => {
    dispatch(updateEstimateReminders(updatedReminders));
    setIsReminderModalOpen(false);
  };

  const handleTabChange = (tab: CreateEditEstimateNavEnum) => {
    setSelectedEstimateTab(tab);
  };

  return (
    <>
      <CreateEstimateContext.Provider value={{ selectedEstimateTab, setSelectedEstimateTab }}>
        {isInitialisingEstimateFromAPI || isFetchingCustomer ? (
          <Loader />
        ) : (
          <div className="relative flex h-full w-full flex-col">
            <CreateEstimateHeader
              isEdit={isEdit}
              onSave={onCreateAndShare}
              isActionInProgress={isSaveInProgress}
              onPreview={onPreview}
            />
            <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
              <div className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0 lg:flex lg:w-80">
                <CreateEstimateSidebar activeTab={selectedEstimateTab} onTabChange={handleTabChange} />
              </div>
              <div
                id="scrollable-estimate-div"
                className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 lg:pl-0 lg:pt-9">
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.CUSTOMER}>
                  <EstimateCustomer />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.ESTIMATE_DETAILS}>
                  <CreateEstimateDetails context={CreateEstimateContext} />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.LINE_ITEMS}>
                  <EstimateLineItemSection
                    isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                    context={CreateEstimateContext}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.NOTES_ATTACHMENTS}>
                  <EstimateNotesAndAttachmentsSection
                    context={CreateEstimateContext}
                    termsAndConditions={merchantCustomisationsSuccess ? merchantCustomisations.estimateTnc : null}
                    isLoadingTermsAndConditions={isFetchingMerchantCustomisations}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col">
                  <EstimateFormRemindersSection
                    reminders={reminders}
                    isEnabled={remindersEnabled}
                    onToggle={() => dispatch(updateRemindersEnabled(!remindersEnabled))}
                    onOpenModal={() => setIsReminderModalOpen(true)}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col gap-y-8">
                  <CreateEstimateMoreOptions />
                </div>
              </div>
            </div>
          </div>
        )}
      </CreateEstimateContext.Provider>

      {isReminderModalOpen && (
        <EditReminderModal
          isOpen={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
          onSave={handleSaveReminders}
          title="Estimate reminders"
          subtitle="Reminders will be sent to customer's email"
          referenceDate={expiryDate?.toISOString() || ''}
          initialReminders={reminders}
          dateType={ReminderDateTypeLabel.EXPIRY_DATE}
        />
      )}
    </>
  );
};

export default CreateEstimate;
